import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  Timestamp,
  collection,
  Unsubscribe,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { COLLECTIONS } from '@/lib/db/collections';
import { UserProfile } from '../types';
import { handleError } from '@/lib/utils/errors';
import { StripeSubscription } from '@/lib/db/services/stripeSubscription';

export async function initializeUserProfile(
  uid: string,
  email: string,
  displayName?: string
): Promise<void> {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      const profile: UserProfile = {
        uid,
        email,
        displayName,
        createdAt: Timestamp.now(),
        lastLoginAt: Timestamp.now(),
      };

      await setDoc(userRef, profile);
    } else {
      await setDoc(
        userRef,
        {
          lastLoginAt: Timestamp.now(),
        },
        { merge: true }
      );
    }
  } catch (error) {
    throw handleError(error);
  }
}

interface ListenerCallbacks {
  onUserUpdate?: (user: UserProfile) => void;
  onSubscriptionUpdate?: (subscription: StripeSubscription | null) => void;
  onError: (error: Error) => void;
}

export const createSubscriptionListener = (
  userId: string,
  { onSubscriptionUpdate, onError }: ListenerCallbacks
): Unsubscribe => {
  // Get reference to subscriptions collection
  const subscriptionsRef = collection(db, 'users', userId, 'subscriptions');

  // Create a query to get the most recent subscription
  const latestSubscriptionQuery = query(
    subscriptionsRef,
    orderBy('created', 'desc'),
    limit(1)
  );

  return onSnapshot(
    latestSubscriptionQuery,
    (snapshot) => {
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const subscriptionData = doc.data() as Omit<StripeSubscription, 'id'>;
        onSubscriptionUpdate?.(subscriptionData);
      } else {
        onSubscriptionUpdate?.(null);
      }
    },
    (error) => onError(error)
  );
};

export function subscribeToProfile(
  uid: string,
  callback: (profile: UserProfile | null) => void
): () => void {
  const userRef = doc(db, COLLECTIONS.USERS, uid);

  return onSnapshot(
    userRef,
    (doc) => callback(doc.exists() ? (doc.data() as UserProfile) : null),
    (error) => {
      console.error('Profile subscription error:', error);
      callback(null);
    }
  );
}
